import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { fileToDataUrl } from "../../lib/admin";

const empty = {
  site_title: "",
  meta_description: "",
  favicon: "",
  logo: "",
  description: "",
  email: "",
  phone: "",
  address: "",
  instagram: "",
  facebook: "",
  youtube: "",
  linkedin: "",
};

export default function AdminSettings() {
  const [brands, setBrands] = useState([]);
  const [selected, setSelected] = useState("_all");
  const [form, setForm] = useState(empty);

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data));
  }, []);

  useEffect(() => {
    api.get(`/settings/${selected}`).then((r) => {
      setForm({ ...empty, ...r.data });
    });
  }, [selected]);

  const save = async () => {
    try {
      await api.put(`/settings/${selected}`, { ...form, brand_slug: selected });
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onFile = async (field, file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setForm({ ...form, [field]: url });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="admin-settings">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Configuration</div>
          <h1 className="font-display text-4xl tracking-tighter">Site Settings</h1>
        </div>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="adm-input min-w-[220px]"
          data-testid="settings-brand-select"
        >
          <option value="_all">Main Site (All)</option>
          {brands.map((b) => (
            <option key={b.id} value={b.slug}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      {selected !== "_all" && (
        <div className="text-xs text-zinc-500 mb-4">
          Empty fields will fall back to the Main Site settings.
        </div>
      )}

      <Section title="Identity">
        <Grid>
          <Field label="Site Title">
            <input value={form.site_title} onChange={set("site_title")} className="adm-input" data-testid="settings-title" />
          </Field>
          <Field label="Meta Description">
            <input value={form.meta_description} onChange={set("meta_description")} className="adm-input" />
          </Field>
          <Field label="Description (footer)" full>
            <textarea rows={3} value={form.description} onChange={set("description")} className="adm-input" data-testid="settings-description" />
          </Field>
          <Field label="Logo">
            <FileRow value={form.logo} onPick={(f) => onFile("logo", f)} testid="settings-logo-input" />
          </Field>
          <Field label="Favicon">
            <FileRow value={form.favicon} onPick={(f) => onFile("favicon", f)} small />
          </Field>
        </Grid>
      </Section>

      <Section title="Contact">
        <Grid>
          <Field label="Email">
            <input type="email" value={form.email} onChange={set("email")} className="adm-input" data-testid="settings-email" />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={set("phone")} className="adm-input" data-testid="settings-phone" />
          </Field>
          <Field label="Address" full>
            <textarea rows={2} value={form.address} onChange={set("address")} className="adm-input" data-testid="settings-address" />
          </Field>
        </Grid>
      </Section>

      <Section title="Social Media">
        <Grid>
          <Field label="Instagram URL">
            <input value={form.instagram} onChange={set("instagram")} className="adm-input" data-testid="settings-instagram" placeholder="https://instagram.com/..." />
          </Field>
          <Field label="Facebook URL">
            <input value={form.facebook} onChange={set("facebook")} className="adm-input" data-testid="settings-facebook" placeholder="https://facebook.com/..." />
          </Field>
          <Field label="YouTube URL">
            <input value={form.youtube} onChange={set("youtube")} className="adm-input" data-testid="settings-youtube" placeholder="https://youtube.com/@..." />
          </Field>
          <Field label="LinkedIn URL">
            <input value={form.linkedin} onChange={set("linkedin")} className="adm-input" data-testid="settings-linkedin" placeholder="https://linkedin.com/company/..." />
          </Field>
        </Grid>
      </Section>

      <button onClick={save} className="brand-btn mt-2 px-6 py-3 text-xs uppercase tracking-[0.2em]" data-testid="settings-save-btn">
        Save
      </button>
      <style>{`.adm-input { width: 100%; background: #141414; border: 1px solid rgba(255,255,255,0.1); padding: 0.625rem 0.875rem; font-size: 0.875rem; color: white; outline: none; } .adm-input:focus { border-color: rgba(255,255,255,0.3); }`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-6 mb-6">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-5">{title}</div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}

function FileRow({ value, onPick, testid, small }) {
  return (
    <div className="flex items-center gap-3">
      {value && <img src={value} className={small ? "h-8 w-8 object-contain" : "h-10 w-auto object-contain"} alt="" />}
      <input
        type="file"
        accept="image/*"
        onChange={(e) => onPick(e.target.files?.[0])}
        className="text-xs text-zinc-400"
        data-testid={testid}
      />
    </div>
  );
}
